import { invalidateAllResourceAudience } from "@gram/client/react-query/resourceAudience.js";
import { Badge } from "@/components/ui/Badge";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import type { Role } from "@gram/client/models/components/role.js";
import {
  invalidateAllRoles,
  useRoles,
} from "@gram/client/react-query/roles.js";
import {
  invalidateAllMembers,
  useMembers,
} from "@gram/client/react-query/members.js";
import { useDeleteRoleMutation } from "@gram/client/react-query/deleteRole.js";
import { SkeletonTable } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Icon } from "@/components/ui/Icon";
import { useOrgRoutes } from "@/routes";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { DeleteRoleDialog } from "./DeleteRoleDialog";
import { MemberFacepile } from "@/components/member-facepile";
import { Ellipsis } from "lucide-react";
import { RequireScope } from "@/components/require-scope";
import { TableRowContextMenu } from "@/components/table-row-context-menu";
import type { Action } from "@/components/ui/MoreActions";
import { useRBAC } from "@/hooks/useRBAC";
import { cn } from "@/lib/utils";
import { visiblePermissionCount } from "./roleDialogState";
import { allMemberRoleIds } from "@/lib/member-roles";
import { pluralize } from "@/lib/format";

// Single source of truth for the per-role actions: the "⋯" dropdown and the
// row's right-click context menu both render from this list. Edit always;
// Delete only for non-system roles.
function roleActions(
  role: Role,
  { onEdit, onDelete }: { onEdit: () => void; onDelete: () => void },
): Action[] {
  return [
    { label: "Edit", onClick: onEdit },
    ...(!role.isSystem
      ? [{ label: "Delete", destructive: true, onClick: onDelete }]
      : []),
  ];
}

function RoleActionsMenu({
  role,
  onEdit,
  onDelete,
}: {
  role: Role;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const actions = roleActions(role, { onEdit, onDelete });

  return (
    // Render-prop form: the dropdown content is portaled to <body>, so it
    // escapes the pointer-events containment of the node form. Disabling the
    // Radix trigger directly is what actually prevents the menu from opening.
    <RequireScope scope="org:admin" level="component">
      {({ disabled }) => (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "text-muted-foreground hover:bg-accent hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center transition-colors",
                open && "bg-accent text-foreground",
                disabled && "cursor-not-allowed",
              )}
            >
              <Ellipsis className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action) => (
              <DropdownMenuItem
                key={action.label}
                onSelect={() => {
                  void setTimeout(action.onClick, 0);
                }}
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </RequireScope>
  );
}

type RowMember = {
  id: string;
  name: string;
  email: string;
  photoUrl?: string;
  roleIds: string[];
  directoryRoleIds: string[];
};

function RoleRow({
  role,
  members,
  canManageRoles,
  onEdit,
  onDelete,
}: {
  role: Role;
  members: RowMember[];
  canManageRoles: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const roleMembers = members
    .filter((m) => allMemberRoleIds(m).includes(role.id))
    .map((m) => ({
      id: m.id,
      name: m.name,
      email: m.email,
      photoUrl: m.photoUrl,
    }));
  // The faces are everyone who holds the role. When a directory mapping gives
  // it to some of them, say how many hold it directly, since only those
  // assignments can be changed on the member.
  const directCount = members.filter((m) => m.roleIds.includes(role.id)).length;
  const mappedOnly = roleMembers.length - directCount;

  // Mirror the row-actions menu via the shared builder. Without org:admin the
  // menu is empty and the row stays unwrapped.
  const actions: Action[] = canManageRoles
    ? roleActions(role, { onEdit, onDelete })
    : [];

  return (
    <TableRowContextMenu actions={actions}>
      <div
        role={canManageRoles ? "button" : undefined}
        tabIndex={canManageRoles ? 0 : undefined}
        onClick={canManageRoles ? onEdit : undefined}
        onKeyDown={
          canManageRoles
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onEdit();
                }
              }
            : undefined
        }
        className={cn(
          "border-border col-span-full grid grid-cols-subgrid items-center gap-x-6 border-b px-4 py-3 last:border-b-0",
          canManageRoles && "hover:bg-muted/50 cursor-pointer",
        )}
      >
        <div className="flex items-center gap-2">
          <Text variant="body" className="font-medium">
            {role.name}
          </Text>
          {role.isSystem && (
            <Badge variant="neutral" size="sm">
              System
            </Badge>
          )}
        </div>
        <Text variant="body" className="text-muted-foreground min-w-0 truncate">
          {role.description}
        </Text>
        <Text variant="body">{visiblePermissionCount(role.grants)}</Text>
        <div className="flex items-center gap-2">
          <MemberFacepile members={roleMembers} />
          {mappedOnly > 0 && (
            <Text
              muted
              small
              className="whitespace-nowrap"
              title={`${pluralize(roleMembers.length, "member")} in total: ${directCount} assigned directly, ${mappedOnly} only through directory role mappings.`}
            >
              {directCount} direct · {mappedOnly} via directory
            </Text>
          )}
        </div>
        <div aria-hidden />
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <RoleActionsMenu role={role} onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
    </TableRowContextMenu>
  );
}

export function RolesTab(): JSX.Element {
  const { hasAnyScope } = useRBAC();
  // Mirror the row-actions menu gate (org:admin): without it, the row is not
  // clickable and shows no affordance.
  const canManageRoles = hasAnyScope(["org:admin"]);
  const navigate = useNavigate();
  const orgRoutes = useOrgRoutes();
  // Authoring a role is its own page: a sheet cannot hold a role's permissions
  // and their rules without scrolling away the thing being edited.
  // A string, so the deep-link effect below can depend on it: the routes
  // object itself is rebuilt every render.
  const rolesHref = orgRoutes.access.roles.href();
  const openRoleEditor = (role: Role | null) =>
    void navigate(
      role ? `${rolesHref}/${role.id}/edit` : `${rolesHref}/create`,
    );
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: rolesData, isLoading } = useRoles();
  const roles = [...(rolesData?.roles ?? [])].sort(
    (a, b) => Number(b.isSystem) - Number(a.isSystem),
  );
  const { data: membersData } = useMembers();
  const members = membersData?.members ?? [];

  useEffect(() => {
    const editRoleId = searchParams.get("editRole");
    if (editRoleId && roles.length > 0) {
      const role = roles.find((r) => r.id === editRoleId);
      if (role) {
        // Mirror the row/menu gate (org:admin) on the deep-link path too
        // (still consume the param so it doesn't linger in the URL).
        if (canManageRoles) void navigate(`${rolesHref}/${role.id}/edit`);
        setSearchParams(
          (prev) => {
            prev.delete("editRole");
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [
    searchParams,
    roles,
    setSearchParams,
    canManageRoles,
    navigate,
    rolesHref,
  ]);

  const defaultRole =
    roles.find((r) => r.isSystem && r.name === "Member") ?? null;

  const membersOfDeletingRole = deletingRole
    ? members.filter((m) => m.roleIds.includes(deletingRole.id))
    : [];

  const deleteRole = useDeleteRoleMutation({
    onSuccess: async () => {
      await Promise.all([
        invalidateAllRoles(queryClient),
        invalidateAllResourceAudience(queryClient),
        invalidateAllMembers(queryClient),
      ]);
    },
  });

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div>
          <Heading variant="h4">Roles</Heading>
          <Text muted small className="mt-1">
            Define roles and their associated permissions.
          </Text>
        </div>
        <RequireScope scope="org:admin" level="component">
          <Button onClick={() => openRoleEditor(null)}>
            <Button.LeftIcon>
              <Icon name="plus" className="h-4 w-4" />
            </Button.LeftIcon>
            <Button.Text>Add Role</Button.Text>
          </Button>
        </RequireScope>
      </div>

      {isLoading ? (
        <div className="mt-4">
          <SkeletonTable />
        </div>
      ) : (
        // Grid table: the parent owns the column tracks and each row is a
        // subgrid spanning them, so cells align across rows. Description uses
        // minmax(0,1fr) (shrinks, absorbs slack); Members uses max-content
        // (sizes to the bounded facepile) — neither can overflow the table.
        <div className="border-border mt-4 grid grid-cols-[max-content_minmax(0,24rem)_max-content_max-content_1fr_max-content] overflow-hidden border">
          <div className="border-border col-span-full grid grid-cols-subgrid items-center gap-x-6 border-b px-4 py-2.5">
            <div className="text-eyebrow">Name</div>
            <div className="text-eyebrow">Description</div>
            <div className="text-eyebrow">Permissions</div>
            <div className="text-eyebrow">Members</div>
            <div aria-hidden />
            <div className="sr-only">Actions</div>
          </div>
          {roles.length === 0 ? (
            <div className="text-muted-foreground col-span-full p-4">
              No roles have been created yet.
            </div>
          ) : (
            roles.map((role) => (
              <RoleRow
                key={role.id}
                role={role}
                members={members}
                canManageRoles={canManageRoles}
                onEdit={() => openRoleEditor(role)}
                onDelete={() => setDeletingRole(role)}
              />
            ))
          )}
        </div>
      )}

      <div className="border-border/50 bg-muted/30 mt-8 border px-4 py-3">
        <Text variant="subheading" className="mb-4">
          About System roles
        </Text>
        <div className="flex items-start gap-3 text-sm">
          <Badge
            variant="neutral"
            size="sm"
            className="bg-surface-primary-default mt-0.5 w-16 shrink-0 [&>span]:text-center"
          >
            Member
          </Badge>
          <Text variant="body" className="text-muted-foreground text-sm">
            The default role for most users. Grants read access across the
            organization and projects. Gives the ability to connect to MCP
            servers and other resources.
          </Text>
        </div>
        <div className="mt-2 flex items-start gap-3 text-sm">
          <Badge
            variant="neutral"
            size="sm"
            className="bg-surface-primary-default mt-0.5 w-16 shrink-0 [&>span]:text-center"
          >
            Admin
          </Badge>
          <Text variant="body" className="text-muted-foreground text-sm">
            Full access to all organization settings, billing, member
            management, every project, MCP server, skills and assistants.
          </Text>
        </div>
      </div>

      <DeleteRoleDialog
        isOpen={!!deletingRole}
        onOpenChange={(open) => {
          if (!open) setDeletingRole(null);
        }}
        handleDeleteRole={() => {
          void (async () => {
            if (deletingRole) {
              await deleteRole.mutateAsync({
                request: { id: deletingRole.id },
              });
              setDeletingRole(null);
            }
          })();
        }}
        handleCancel={() => setDeletingRole(null)}
        role={deletingRole}
        members={membersOfDeletingRole}
        defaultRole={defaultRole}
      />
    </div>
  );
}
